// Admin CRUD over custom dashboard cards (Phase 2 of dashboard
// customization). Mirrors `crates/tres-api/src/handlers/dashboard_cards.rs`.
//
// Read hook: useDashboardCards() — lists raw card definitions + which
// built-ins are currently hidden. PowerUser+ only.
//
// Mutations are plain async functions (createCard / patchCard /
// deleteCard / hideBuiltin / unhideBuiltin) — same shape as useVocab's
// create / patch / delete helpers.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { apiFetch } from '@/lib/api';

export type CardKind = 'count' | 'distribution' | 'table';

export interface CardDef {
  id: string;
  title: string;
  subtitle: string;
  kind: CardKind;
  sparql: string;
  ordinal: number;
  dashboard: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CardListResponse {
  cards: CardDef[];
  hidden_builtins: string[];
  deleted_cards: CardDef[];
}

export function useDashboardCards(enabled: boolean) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  return useQuery<CardListResponse>({
    queryKey: ['dashboard-cards', token ?? 'anon'],
    enabled: enabled && !!token,
    staleTime: 30_000,
    queryFn: async () => {
      if (!token) throw new Error('not authenticated');
      const r = await apiFetch<CardListResponse>('/api/v1/dashboard/cards', token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

export interface CreateCardRequest {
  title: string;
  subtitle?: string | undefined;
  kind: CardKind;
  sparql: string;
  ordinal?: number | undefined;
  dashboard?: string;
}

export async function createCard(body: CreateCardRequest, accessToken: string) {
  return apiFetch<CardDef>('/api/v1/dashboard/cards', accessToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface PatchCardRequest {
  title?: string;
  subtitle?: string;
  sparql?: string;
  ordinal?: number;
  dashboard?: string;
}

export async function patchCard(id: string, body: PatchCardRequest, accessToken: string) {
  // The backend uses :id in the path. URL-encode in case the id ever
  // contains characters that need it (today it's always urn:uuid:<v4>
  // which only has [a-f0-9-:] but encoding is defensive).
  return apiFetch<null>(`/api/v1/dashboard/cards/${encodeURIComponent(id)}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteCard(id: string, accessToken: string) {
  return apiFetch<null>(`/api/v1/dashboard/cards/${encodeURIComponent(id)}`, accessToken, {
    method: 'DELETE',
  });
}

export async function restoreCard(id: string, accessToken: string) {
  return apiFetch<null>(`/api/v1/dashboard/cards/${encodeURIComponent(id)}/restore`, accessToken, {
    method: 'POST',
  });
}

export async function hideBuiltin(builtinId: string, accessToken: string) {
  return apiFetch<null>(
    `/api/v1/dashboard/builtins/${encodeURIComponent(builtinId)}/hide`,
    accessToken,
    { method: 'POST' },
  );
}

export async function unhideBuiltin(builtinId: string, accessToken: string) {
  return apiFetch<null>(
    `/api/v1/dashboard/builtins/${encodeURIComponent(builtinId)}/hide`,
    accessToken,
    { method: 'DELETE' },
  );
}
